import TomSelect from 'tom-select';
import 'tom-select/dist/css/tom-select.css';
import Alpine from 'alpinejs';
import 'htmx.org';
import Plotly from 'plotly.js-dist-min';

// Make libraries available globally
window.Alpine = Alpine;
window.Plotly = Plotly;
window.TomSelect = TomSelect;

// Initialize Alpine store for sidebar state
document.addEventListener('DOMContentLoaded', () => {
    // Initialize Alpine
    Alpine.store('sidebar', {
        open: false
    });

    Alpine.store('chart', {
        loading: true,
        isGenerating: false,
        hasData: false,
        hasIndicators: false,
        hasDataset: false
    });

    // Start Alpine after DOM is loaded
    Alpine.start();
});

// Make our functions globally available
window.clearIndicatorsAndChart = clearIndicatorsAndChart;
window.initDatasetSelect = initDatasetSelect;
window.clearChart = clearChart;
window.initIndicatorSelect = initIndicatorSelect;
window.handleTransformationSelection = handleTransformationSelection;

// Add these functions at the top level
function updateUrlParams(params = {}) {
    // Create new URL object
    const url = new URL(window.location);

    if (Object.keys(params).length === 0) {
        // If no params, remove all query parameters
        url.search = '';
    } else {
        // Update or add new parameters
        Object.entries(params).forEach(([key, value]) => {
            if (value) {
                url.searchParams.set(key, value);
            } else {
                url.searchParams.delete(key);
            }
        });
    }

    // Update URL without reloading the page
    window.history.replaceState({}, '', url);
}

// Tom Select initialization and utilities
function clearIndicatorsAndChart() {
    const indicatorsSelect = document.querySelector('#indicators');
    if (indicatorsSelect.tomselect) {
        indicatorsSelect.tomselect.destroy();
    }

    document.getElementById("indicators-container").innerHTML = `
        <label for="indicators" class="sidebar-section-label">
            <svg class="sidebar-section-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Indicadores
        </label>
        <select id="indicators" name="indicators_id" multiple disabled class="sidebar-select">
            <option value="">Seleccionar Indicadores</option>
        </select>
    `;

    updateUrlParams();

    // Reset hasIndicators state
    Alpine.store('chart').hasIndicators = false;

    // Keep loading state when clearing
    startChartLoading();

    // Clear any error messages
    if (typeof window.clearErrorMessages === 'function') {
        window.clearErrorMessages();
    }

    // Reinitialize TomSelect for the indicators dropdown even when it's disabled
    // This ensures the UI is consistent
    requestAnimationFrame(() => {
        initIndicatorSelect();
    });
}

function clearChart() {
    const chartContainer = document.getElementById('chart-container');
    if (chartContainer) {
        Plotly.purge(chartContainer);
    }
    Alpine.store('chart').hasData = false;
    // Keep loading state when clearing
    startChartLoading();

    // Clear any error messages
    if (typeof window.clearErrorMessages === 'function') {
        window.clearErrorMessages();
    }
}

// Update the area dropdown initialization
document.addEventListener('DOMContentLoaded', function() {
    // Only initialize TomSelect if we're on the Explorer page
    if (!document.getElementById('area')) return;

    requestAnimationFrame(() => {
        // Initialize area dropdown
        const areaSelect = new TomSelect('#area', {
            placeholder: 'Seleccionar área'
        });

        // Add change handler for area
        areaSelect.on('change', function(value) {
            if (!value) {
                updateUrlParams(); // Clear all params if no area selected
            } else {
                // Don't update URL here - wait for dataset select to be replaced
                // The dataset select's onChange handler will update the URL
                updateUrlParams({ area_id: value, dataset_id: null });
            }
        });

        // Initialize dataset select regardless of disabled state
        const datasetSelect = document.getElementById('dataset');
        if (datasetSelect) {
            const ts = initDatasetSelect();
            if (datasetSelect.disabled) {
                ts.disable();
            }
        }

        // Initialize indicators select regardless of disabled state
        const indicatorsSelect = document.getElementById('indicators');
        if (indicatorsSelect) {
            const ts = initIndicatorSelect();
            if (indicatorsSelect.disabled) {
                ts.disable();
            }
        }
    });
});

// Function to safely destroy TomSelect instance
function safeDestroyTomSelect(element) {
    if (element && element.tomselect) {
        element.tomselect.destroy();
    }
}

// Function to initialize Tom Select for datasets
function initDatasetSelect() {
    const select = document.getElementById('dataset');
    if (!select) return;

    select.addEventListener('change', function() {
        // Update the hasDataset state when a dataset is selected
        Alpine.store('chart').hasDataset = !!this.value;
    });

    safeDestroyTomSelect(select);

    const ts = new TomSelect('#dataset', {
        placeholder: 'Seleccionar dataset',
        render: {
            item: function(data, escape) {
                return `<div>${escape(data.text)}</div>`;
            }
        },
        onChange: function(value) {
            const areaSelect = document.getElementById('area');
            if (!value) {
                updateUrlParams({ area_id: areaSelect.value }); // Keep area, remove dataset
            } else {
                updateUrlParams({
                    area_id: areaSelect.value,
                    dataset_id: value
                });
            }
        }
    });

    return ts;
}

// Function to initialize Tom Select for indicators
function initIndicatorSelect() {
    const existingSelect = document.querySelector('#indicators');
    if (!existingSelect) return;

    // Destroy existing instance if it exists
    if (existingSelect.tomselect) {
        existingSelect.tomselect.destroy();
    }

    const select = new TomSelect('#indicators', {
        plugins: ['remove_button'],
        maxItems: null,
        render: {
            item: function(data, escape) {
                return `<div>${escape(data.text)}</div>`;
            }
        },
        onChange: function() {
            // Update hasIndicators state based on selection
            Alpine.store('chart').hasIndicators = this.items.length > 0;
        }
    });

    // Initialize state
    Alpine.store('chart').hasIndicators = select.items.length > 0;

    // If the original select was disabled, disable the TomSelect instance too
    if (existingSelect.disabled) {
        select.disable();
    }

    return select;
}

// Transformation handling
function handleTransformationSelection(checkbox) {
    if (!checkbox.checked) return; // Don't do anything if unchecking

    const transformGroup = checkbox.closest('.transformation-group');
    const otherCheckboxes = transformGroup.querySelectorAll('input[type="checkbox"]');

    // Uncheck other checkboxes in the same group
    otherCheckboxes.forEach(cb => {
        if (cb !== checkbox) {
            cb.checked = false;
            // Clear the parameters container if there is one
            const paramsContainer = cb.closest('.transform-option').nextElementSibling;
            if (paramsContainer && paramsContainer.classList.contains('params-container')) {
                paramsContainer.innerHTML = '';
            }
        }
    });
}

// Move the event listener inside DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
    // Only attach the event listener if we're on the Explorer page
    if (document.getElementById('transformations-container')) {
        document.body.addEventListener('htmx:afterSettle', (event) => {
            // Only handle events from the add transformation button
            if (event.detail.requestConfig?.path === '/explorer/get-transformation-select') {
                const groups = document.querySelectorAll('.transformation-group');
                const newGroup = groups[groups.length - 1];

                // Collapse all other groups
                groups.forEach(group => {
                    if (group !== newGroup) {
                        group.dispatchEvent(new CustomEvent('transformation-collapse', {
                            detail: { target: newGroup }
                        }));
                    }
                });
            }
        });
    }
});

// Add this event listener for handling expansion of existing transformations
document.addEventListener('click', (event) => {
    const button = event.target.closest('[x-on\\:click*="expanded = !expanded"]');
    if (button) {
        const currentGroup = button.closest('.transformation-group');
        const isExpanding = !currentGroup._x_dataStack[0].expanded;

        if (isExpanding) {
            document.querySelectorAll('.transformation-group').forEach(group => {
                if (group !== currentGroup) {
                    group.dispatchEvent(new CustomEvent('transformation-collapse', {
                        detail: { target: currentGroup }
                    }));
                }
            });
        }
    }
});

// Add event listener for when chart is updated
document.addEventListener('htmx:afterSwap', function(evt) {
    // When dataset select is swapped in
    if (evt.detail.target.id === 'datasets-container') {
        initDatasetSelect();
    }

    // When indicators are swapped in
    if (evt.detail.target.id === 'indicators-container') {
        initIndicatorSelect();
    }

    // When chart section is updated
    if (evt.detail.target.id === 'chart-section') {
        // Scroll to top of the page smoothly
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Close sidebar on mobile
        if (window.innerWidth < 1024) {
            if (Alpine) {
                Alpine.store('sidebar', {
                    open: false
                });
            }
        }
    }
});

// Add loading state handlers
window.startChartLoading = function() {
    Alpine.store('chart').loading = true;
    Alpine.store('chart').hasData = false;

    // Clear any error messages when starting a new load
    if (typeof window.clearErrorMessages === 'function') {
        window.clearErrorMessages();
    }
};

window.startChartGeneration = function() {
    Alpine.store('chart').loading = true;
    Alpine.store('chart').hasData = false;
    Alpine.store('chart').isGenerating = true;

    // Disable scrolling while loading
    document.body.style.overflow = 'hidden';
};

window.stopChartLoading = function() {
    Alpine.store('chart').loading = false;
    Alpine.store('chart').isGenerating = false;
};

window.setChartData = function() {
    Alpine.store('chart').loading = false;
    Alpine.store('chart').hasData = true;
    Alpine.store('chart').isGenerating = false;

    // Re-enable scrolling
    document.body.style.overflow = '';
};